import React from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { styled } from '@linaria/react';
import { COLORS, Pill, DirectionalIcon } from '@livecommerce/design-system';

import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import { buildRoute } from 'constants/routes.js';
import { useNavigationFrameContext } from 'contexts/NavigationFrameContext.jsx';
import { Flair } from 'components-v3/Flairs/Flair.jsx';
import { useDelegatedFrameContext } from 'contexts/DelegatedFrameContext.jsx';

const StyledIcon = styled(DirectionalIcon)`
  width: 24px;
`;

const StyledLink = styled(NavLink)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px;
  border-radius: 8px;
  text-decoration: none;
  color: ${COLORS.white};

  ${StyledIcon} {
    color: transparent;
  }

  &.active {
    ${StyledIcon} {
      color: inherit;
    }
  }

  &.active,
  &:hover {
    background-color: ${COLORS.white};
    color: ${COLORS.dark_700};
  }
`;

const StyledFlair = styled(Flair)`
  display: inline-block;
  vertical-align: bottom;
  height: 12px;
  width: 12px;
`;

const StyledPill = styled(Pill)`
  overflow: hidden;
  display: inline-block;
  text-overflow: ellipsis;
`;

const StyledSpan = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SubMenuItem = ({ disabled, subMenuItem, ref, flair }) => {
  const { t } = useLocalization();
  const { setSelectedNavigationItemTitle } = useNavigationFrameContext();
  const { title, route, routeParams, pills = [], exact = false } = subMenuItem;
  const { selectedProduct, orgId, clientId } = useProductDataOutsideRoutes();
  const { delegatedFrame } = useDelegatedFrameContext();

  const handleSubmenuLinkClick = (e, title, builtRoute, route) => {
    if (disabled) return;

    setSelectedNavigationItemTitle(null);

    trackingService.track({
      eventType: 'sidebar-navigation:submenu',
      destination: title,
      path: builtRoute,
    });

    if (delegatedFrame?.supportsRoute(route)) {
      delegatedFrame.changeRoute(builtRoute);
      e.preventDefault();
      return false;
    }
  };

  const builtRoute = buildRoute(route, { orgId, selectedProduct, clientId, ...routeParams });

  return (
    <li key={title} data-test-id={`${title}SidebarSubMenuItem`} ref={ref}>
      <StyledLink to={builtRoute} onClick={(e) => handleSubmenuLinkClick(e, title, builtRoute, route)} exact={exact}>
        <StyledIcon name="arrowRegular" direction="right" />
        <StyledSpan>
          {title}
          {flair && <StyledFlair {...flair} outerColor="transparent" />}
        </StyledSpan>
        {pills.map(pill => {
          return (
            <StyledPill
              variant={pill?.variant}
              backgroundColor={pill?.backgroundColor}
              color={pill?.color}
            >
              {t(pill.label)}
            </StyledPill>
          );
        })}
      </StyledLink>
    </li>
  );
};

SubMenuItem.propTypes = {
  disabled: PropTypes.bool,
  subMenuItem: PropTypes.object,
};

export default React.forwardRef((props, ref) => <SubMenuItem {...props} forwardedRef={ref} />);
